import { useCallback } from 'react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';

import ChevronRightIcon from 'modules/common/components/Icons/ChevronRightIcon';

// Intentionally narrower than the 336px task card — a compact end-of-scroller cap.
const CARD_WIDTH = 146;
const CARD_MIN_HEIGHT = 144;

/**
 * Compact end-of-carousel card showing how many prioritized tasks sit beyond the shown top-N, with a
 * "See all" CTA to the full Tasks page. Rendered as the final scroller item only when count > 0.
 * When `area` is set (feature-page carousels), the CTA deep-links with that area filter pre-applied.
 */
export default function RemainingCard({ count, area }: Readonly<{ count: number; area?: string }>) {
    const history = useHistory();
    const handleSeeAll = useCallback(
        () => history.push({ pathname: '/tasks', search: area ? `?area=${area}` : '' }),
        [history, area]
    );

    return (
        <Paper
            sx={{
                width: CARD_WIDTH,
                flexShrink: 0,
                minHeight: CARD_MIN_HEIGHT,
                p: 2,
                borderRadius: 4,
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Stack sx={{ flex: 1 }}>
                <Stack>
                    <Typography variant="h2">{count}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {count === 1 ? 'task remaining' : 'tasks remaining'}
                    </Typography>
                </Stack>
                <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    fullWidth
                    onClick={handleSeeAll}
                    endIcon={<ChevronRightIcon />}
                    sx={{ mt: 'auto' }}>
                    See all
                </Button>
            </Stack>
        </Paper>
    );
}
